import { useState } from "react";
import { ListFilter, ArrowUp, ArrowDown, Check, Search, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

/** Excel-style column header menu: sort + (multi/single) value filter. */
export function ColumnMenu({ sortKey, sortBy, sortDir, onSort, filterKey, options = [], selected, single = false, onFilter }) {
  const [q, setQ] = useState("");
  const sortActive = sortKey && sortBy === sortKey;
  const filterActive = single ? !!selected : (selected && selected.length > 0);
  const active = sortActive || filterActive;
  const tid = filterKey || sortKey;

  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const allOn = !single && (!selected || selected.length === 0);

  const toggle = (v) => {
    if (single) { onFilter(selected === v ? "" : v); return; }
    const base = selected || [];
    onFilter(base.includes(v) ? base.filter((x) => x !== v) : [...base, v]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`col-menu-${tid}`}
          onClick={(e) => e.stopPropagation()}
          className={`ml-1 inline-flex items-center justify-center h-5 w-5 rounded-sm transition-colors ${active ? "text-[#2563EB] bg-[#2563EB]/10" : "text-slate-300 hover:text-slate-500 hover:bg-slate-200/60"}`}>
          <ListFilter size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 rounded-sm p-0" data-testid={`col-menu-${tid}-content`}>
        {sortKey && (
          <div className="p-1.5 border-b border-slate-100">
            <button data-testid={`col-sort-asc-${sortKey}`} onClick={() => onSort(sortKey, "asc")}
              className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-sm hover:bg-slate-50 ${sortActive && sortDir === "asc" ? "text-[#2563EB] font-medium" : "text-slate-600"}`}>
              <ArrowUp size={14} /> Ordenar ascendente
            </button>
            <button data-testid={`col-sort-desc-${sortKey}`} onClick={() => onSort(sortKey, "desc")}
              className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-sm hover:bg-slate-50 ${sortActive && sortDir === "desc" ? "text-[#2563EB] font-medium" : "text-slate-600"}`}>
              <ArrowDown size={14} /> Ordenar descendente
            </button>
          </div>
        )}
        {filterKey && (
          <div className="p-1.5">
            {!single && options.length > 6 && (
              <div className="relative mb-1.5">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                  className="h-8 rounded-sm border-slate-300 text-sm pl-7" data-testid={`col-filter-search-${filterKey}`} />
              </div>
            )}
            {!single && (
              <button data-testid={`col-filter-all-${filterKey}`} onClick={() => onFilter([])}
                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-sm hover:bg-slate-50 text-slate-700 border-b border-slate-100">
                <span className={`flex items-center justify-center h-4 w-4 rounded-sm border ${allOn ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>{allOn && <Check size={12} className="text-white" />}</span>
                (Todos)
              </button>
            )}
            <div className="max-h-56 overflow-auto thin-scroll mt-1">
              {filtered.length === 0 ? (
                <div className="px-2 py-2 text-xs text-slate-400">Sin valores</div>
              ) : filtered.map((o) => {
                const on = single ? selected === o.value : (allOn || (selected || []).includes(o.value));
                return (
                  <button key={o.value} data-testid={`col-filter-${filterKey}-${o.value}`} onClick={() => toggle(o.value)}
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-sm hover:bg-slate-50 text-slate-700 text-left">
                    <span className={`flex items-center justify-center h-4 w-4 ${single ? "rounded-full" : "rounded-sm"} border shrink-0 ${on ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>{on && <Check size={12} className="text-white" />}</span>
                    <span className="truncate">{o.label}</span>
                  </button>
                );
              })}
            </div>
            {filterActive && (
              <button data-testid={`col-filter-clear-${filterKey}`} onClick={() => onFilter(single ? "" : [])}
                className="mt-1 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 px-2 py-1">
                <X size={12} /> Limpiar columna
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
